/**
 * 音声フォーマット変換。
 *
 * ブラウザの Web Audio は Float32(-1.0〜1.0)、
 * Alibaba LiveTranslate が要求するのは 16bit signed PCM リトルエンディアン / 16kHz / モノラル。
 * その差を埋めるのがこのファイルの役目。
 */

/** Float32 サンプル列を 16bit signed PCM(リトルエンディアン)へ変換する。 */
export function floatToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    // 範囲外の値をそのまま丸めると桁あふれで轟音になるため、必ずクリップする。
    const clamped = Math.max(-1, Math.min(1, input[i]!));
    // 負側は 32768、正側は 32767 が最大。非対称なので係数を分ける。
    out[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return out;
}

/**
 * 任意のサンプルレートから 16kHz へダウンサンプリングする。
 *
 * 線形補間 + 簡易ローパス。
 * 音声認識の前処理としてはこれで十分で、高品質リサンプラを積むより
 * メインスレッドを止めないことの方が字幕の遅延に効く。
 */
export function resampleTo16k(input: Float32Array, inputRate: number): Float32Array {
  const targetRate = 16000;
  if (inputRate === targetRate) return input;
  if (input.length === 0) return input;

  const ratio = inputRate / targetRate;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);

  // ダウンサンプリング時は折り返し雑音を防ぐため、区間平均で簡易ローパスをかける。
  const needsLowPass = ratio > 1;
  const window = needsLowPass ? Math.floor(ratio) : 1;

  for (let i = 0; i < outLength; i += 1) {
    const position = i * ratio;
    const index = Math.floor(position);
    const frac = position - index;

    if (needsLowPass) {
      let sum = 0;
      let count = 0;
      for (let j = 0; j < window && index + j < input.length; j += 1) {
        sum += input[index + j]!;
        count += 1;
      }
      out[i] = count > 0 ? sum / count : 0;
    } else {
      const a = input[index] ?? 0;
      const b = input[index + 1] ?? a;
      out[i] = a + (b - a) * frac;
    }
  }

  return out;
}

/** Int16Array を送信用の ArrayBuffer へ。リトルエンディアンを明示する。 */
export function pcm16ToBytes(pcm: Int16Array): ArrayBuffer {
  const buffer = new ArrayBuffer(pcm.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < pcm.length; i += 1) {
    view.setInt16(i * 2, pcm[i]!, true);
  }
  return buffer;
}

export interface LevelReading {
  /** 二乗平均平方根。音量メーターの表示に使う。 */
  rms: number;
  /** 区間内の最大絶対値。クリッピング判定に使う。 */
  peak: number;
}

export function measureLevel(input: Float32Array): LevelReading {
  let sumSquares = 0;
  let peak = 0;
  for (let i = 0; i < input.length; i += 1) {
    const v = input[i]!;
    sumSquares += v * v;
    const abs = Math.abs(v);
    if (abs > peak) peak = abs;
  }
  return {
    rms: input.length > 0 ? Math.sqrt(sumSquares / input.length) : 0,
    peak,
  };
}

/** 音量不足の判定しきい値。工場の騒音下でこれを下回ると認識はまず期待できない。 */
export const LOW_LEVEL_RMS = 0.01;
/** クリッピング(音割れ)の判定しきい値。 */
export const CLIPPING_PEAK = 0.98;
